/**
 * ffrRadio — playlist controller that streams MP3 audio extracted from
 * random FFR level SWFs (replaces the dead Icecast stream).
 *
 * Usage:
 *   const radio = new FFRRadio({ onTitleChange: (s) => setTitle(s) });
 *   await radio.start();   // begins playback
 *   radio.stop();          // pauses and discards the current source
 *   radio.setMuted(true);  // mutes without stopping
 *
 * On each track end (or extraction failure), picks another random level
 * from the server-provided list and starts again. Failures bounce to the
 * next pick automatically.
 */
import { extractSwfMp3 } from './swfMp3.js';

interface Opts {
    /** Called whenever a new track starts playing (with a human label). */
    onTitleChange?: (title: string) => void;
    /** Called when the radio fails to play anything (no levels list, etc). */
    onError?: (err: string) => void;
}

interface LevelsList { count: number; base: string; names: string[]; }

function humaniseLevelName(filename: string): string {
    // level_NewYear_2019_SonicVision.swf  →  "New Year 2019 SonicVision"
    return filename
        .replace(/^level_/, '')
        .replace(/\.swf$/, '')
        .replace(/[_-]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}

export class FFRRadio {
    private audio: HTMLAudioElement;
    private list: LevelsList | null = null;
    private currentUrl: string | null = null;
    private playing = false;
    private aborter: AbortController | null = null;
    private opts: Opts;

    constructor(opts: Opts = {}) {
        this.opts = opts;
        this.audio = new Audio();
        this.audio.preload = 'auto';
        this.audio.addEventListener('ended', () => {
            if (this.playing) void this.next();
        });
        this.audio.addEventListener('error', () => {
            if (this.playing) {
                // Try next pick on decode/play error
                setTimeout(() => { if (this.playing) void this.next(); }, 250);
            }
        });
    }

    async ensureList(): Promise<LevelsList> {
        if (this.list) return this.list;
        const res = await fetch('/prochat/api/levels');
        if (!res.ok) throw new Error(`levels list ${res.status}`);
        this.list = await res.json() as LevelsList;
        return this.list;
    }

    private revokeCurrent() {
        if (this.currentUrl) {
            try { URL.revokeObjectURL(this.currentUrl); } catch { /* ignore */ }
            this.currentUrl = null;
        }
    }

    /** Pick a random level, fetch its SWF, extract MP3, set as source, play. */
    private async loadAndPlayOne(): Promise<void> {
        const list = await this.ensureList();
        if (!list.names.length) throw new Error('no levels available');

        // Pick a random pick; retry up to 5 times on extraction failure.
        let pick: string | null = null;
        let mp3:  Uint8Array | null = null;
        const aborter = new AbortController();
        this.aborter?.abort();
        this.aborter = aborter;

        for (let attempt = 0; attempt < 5 && !mp3 && !aborter.signal.aborted; attempt++) {
            pick = list.names[Math.floor(Math.random() * list.names.length)] ?? null;
            if (!pick) continue;
            try {
                const url = list.base + pick;
                const res = await fetch(url, { signal: aborter.signal });
                if (!res.ok) continue;
                const buf = await res.arrayBuffer();
                mp3 = await extractSwfMp3(buf);
            } catch (e) {
                // network / parse / abort — keep trying unless aborted
                if (aborter.signal.aborted) return;
                // eslint-disable-next-line no-console
                console.warn(`[ffrRadio] pick "${pick}" failed:`, e);
            }
        }
        if (aborter.signal.aborted) return;
        if (!mp3 || !pick) {
            this.opts.onError?.('couldn\'t extract MP3 from any level (5 tries)');
            return;
        }

        this.revokeCurrent();
        const blob = new Blob([mp3], { type: 'audio/mpeg' });
        const url = URL.createObjectURL(blob);
        this.currentUrl = url;
        this.audio.src = url;
        this.audio.play().catch(err => {
            // Autoplay block or decode error — kick to next
            // eslint-disable-next-line no-console
            console.warn('[ffrRadio] play() rejected:', err);
            setTimeout(() => { if (this.playing) void this.next(); }, 250);
        });
        this.opts.onTitleChange?.(humaniseLevelName(pick));
    }

    /** Start (or resume) the radio. */
    async start(): Promise<void> {
        if (this.playing) return;
        this.playing = true;
        try {
            await this.loadAndPlayOne();
        } catch (e) {
            this.opts.onError?.(String((e as Error)?.message ?? e));
            this.playing = false;
        }
    }

    /** Stop playback and discard any decoded blob. */
    stop(): void {
        this.playing = false;
        this.aborter?.abort();
        this.aborter = null;
        try { this.audio.pause(); } catch { /* ignore */ }
        this.audio.removeAttribute('src');
        this.revokeCurrent();
        this.opts.onTitleChange?.('');
    }

    /** Skip to the next track (manual or end-triggered). */
    async next(): Promise<void> {
        if (!this.playing) return;
        try {
            await this.loadAndPlayOne();
        } catch (e) {
            this.opts.onError?.(String((e as Error)?.message ?? e));
        }
    }

    setMuted(m: boolean): void { this.audio.muted = m; }
    get isPlaying(): boolean   { return this.playing && !this.audio.paused; }
}
